import React, { useCallback, useEffect, useState } from 'react';
import { behavior } from '../../analytics';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { SIGNUP_ONBOARDING_ENABLED } from '../../config/features';
import Image from '../../components/common/Image';
import UrlInputStep from '../../components/onboarding/UrlInputStep';
import UrlImageSelectionModal from '../../components/product/UrlImageSelectionModal';
import SignupImageRoleStep from '../../components/onboarding/SignupImageRoleStep';
import SignupResearchStep from '../../components/onboarding/SignupResearchStep';
import SignupProductReviewStep from '../../components/onboarding/SignupProductReviewStep';
import SignupBannerGridStep from '../../components/onboarding/SignupBannerGridStep';
import SignupSkipConfirmation from '../../components/onboarding/SignupSkipConfirmation';
import productService from '../../services/api/productService';
import productImageService, {
  ProductImage,
} from '../../services/api/productImageService';
import signupOnboardingService from '../../services/api/signupOnboardingService';
import userService from '../../services/api/userService';

type SignupOnboardingStep =
  | 'url-input'
  | 'image-select'
  | 'logo-select'
  | 'product-image-select'
  | 'researching'
  | 'product-review'
  | 'banner-grid';

interface VisualStep {
  label: string;
  internalSteps: SignupOnboardingStep[];
}
const VISUAL_STEPS: VisualStep[] = [
  { label: 'URL入力', internalSteps: ['url-input'] },
  { label: '画像保存', internalSteps: ['image-select'] },
  { label: '素材指定', internalSteps: ['logo-select', 'product-image-select'] },
  { label: 'AI分析', internalSteps: ['researching'] },
  { label: '内容確認', internalSteps: ['product-review'] },
  { label: 'バナー', internalSteps: ['banner-grid'] },
];
const visualIndex = (step: SignupOnboardingStep) =>
  Math.max(
    0,
    VISUAL_STEPS.findIndex((item) => item.internalSteps.includes(step)),
  );
const PAGE =
  'min-h-screen flex items-center justify-center bg-warm-bg py-6 px-4';
const CARD_BASE =
  'w-full bg-white rounded-2xl shadow-[0_12px_44px_rgba(31,35,55,0.09)] p-[30px] ' +
  'transition-[max-width] duration-[250ms] ease-[ease] max-[640px]:py-5 max-[640px]:px-4';
const CARD_WIDE = 'max-w-[1120px]';
const CARD_NARROW = 'max-w-[640px]';
const LOGO = 'flex justify-center mb-5 [&_img]:h-[52px] [&_img]:w-auto';
const INDICATOR =
  'flex items-start justify-center overflow-x-auto pt-1 px-0.5 pb-5 mb-[14px]';
const INDICATOR_ITEM = 'flex flex-col items-center min-w-[58px]';
const DOT_BASE = 'w-[11px] h-[11px] rounded-full';
const DOT_ON = 'bg-primary';
const DOT_OFF = 'bg-[#dfe2ea]';
const DOT_ACTIVE_RING = 'shadow-[0_0_0_4px_rgba(97,111,201,0.16)]';
const DOT_NO_RING = 'shadow-none';
const STEP_LABEL_BASE = 'text-[0.65rem] mt-[7px] whitespace-nowrap';
const STEP_LABEL_ON = 'text-[#525fae]';
const STEP_LABEL_OFF = 'text-[#9aa0b2]';
const STEP_LABEL_ACTIVE_WEIGHT = 'font-extrabold';
const STEP_LABEL_IDLE_WEIGHT = 'font-medium';
const LINE_BASE = 'w-[30px] h-0.5 mx-0.5 mt-[5px] mb-0 flex-none';
const LINE_DONE = 'bg-primary';
const LINE_TODO = 'bg-[#e2e5ec]';
const CONTENT = 'animate-[sob-fade-in-10_0.28s_ease-out]';
const ERROR_BANNER =
  'py-3 px-[14px] mb-[14px] border border-red-200 rounded-[9px] bg-red-50 text-red-700 text-[0.86rem]';
const CENTER = 'text-center py-[30px] px-0 text-gray-500';
const IMAGE_INTRO = 'text-center py-[22px] px-0';
const INTRO_TITLE = 'text-[1.35rem] text-gray-800 m-0 mb-2';
const INTRO_COPY = 'text-[0.88rem] text-gray-500 leading-[1.7] m-0 mb-5';
const PRIMARY =
  'min-h-[46px] py-0 px-[22px] border-0 rounded-[9px] bg-primary text-white font-extrabold cursor-pointer';
const SKIP =
  'block mx-auto mt-4 mb-0 border-0 bg-transparent bg-none text-[#8d92a3] underline cursor-pointer';

function productNameFromUrl(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, '') || 'マイサービス';
  } catch {
    return 'マイサービス';
  }
}

const SignupOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [step, setStep] = useState<SignupOnboardingStep>('url-input');

  // 行動分析: ステップ到達を記録（admin 行動分析タブの活性化ファネルが参照）
  useEffect(() => {
    behavior.step('onboarding', step, { variant: 'signup' });
  }, [step]);
  const [serviceUrl, setServiceUrl] = useState('');
  const [productId, setProductId] = useState<number | null>(null);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [logoImageId, setLogoImageId] = useState<number | null>(null);
  const [productImageId, setProductImageId] = useState<number | null>(null);
  const [savingRoles, setSavingRoles] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [guardReady, setGuardReady] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      setGuardReady(false);
      return;
    }
    if (!user.organization_id) {
      navigate('/signup/setup', { replace: true });
      return;
    }
    if (!SIGNUP_ONBOARDING_ENABLED) {
      navigate('/home', { replace: true });
      return;
    }
    if (user.onboarding_completed === true) {
      navigate('/home', { replace: true });
      return;
    }
    setGuardReady(true);
  }, [user, navigate]);

  const completeAndOpenPlans = useCallback(async () => {
    await userService.completeOnboarding();
    navigate('/plans?from=signup');
  }, [navigate]);

  const confirmSkip = useCallback(async () => {
    if (skipping) return;
    setSkipping(true);
    try {
      await completeAndOpenPlans();
    } catch {
      setError('スキップ処理に失敗しました。もう一度お試しください。');
      setSkipping(false);
      setSkipOpen(false);
    }
  }, [skipping, completeAndOpenPlans]);

  const handleUrl = useCallback(async (url: string) => {
    setCreating(true);
    setError('');
    setServiceUrl(url);
    try {
      const response = await productService.createFromUrl({
        name: productNameFromUrl(url),
        description: '',
        url,
        start_fetch: false,
      });
      if (!response.data?.id) throw new Error('missing product id');
      setProductId(response.data.id);
      setStep('image-select');
      setImageModalOpen(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || 'サービスの登録に失敗しました。',
      );
    } finally {
      setCreating(false);
    }
  }, []);

  const handleImagesSaved = useCallback((saved: ProductImage[]) => {
    setImages(saved);
    setImageModalOpen(false);
    setLogoImageId(
      saved.find((image) => image.category === 'brand_logo')?.id ?? null,
    );
    setProductImageId(
      saved.find((image) => image.category === 'product_image')?.id ?? null,
    );
    setStep('logo-select');
  }, []);

  const persistRolesAndResearch = useCallback(async () => {
    if (!productId || savingRoles) return;
    setError('');
    setSavingRoles(true);
    try {
      if (productImageId)
        await productImageService.setPrimaryImage(productId, productImageId);
      const response = await productService.getKnowledgeProductById(productId);
      const metadata = { ...(response.data.extended_metadata || {}) };
      metadata.onboarding_assets = {
        logo_image_id: logoImageId,
        product_image_id: productImageId,
      };
      await productService.updateKnowledgeProduct(productId, {
        extended_metadata: metadata,
      });
      setStep('researching');
    } catch {
      setError('選択した画像情報を保存できませんでした。');
    } finally {
      setSavingRoles(false);
    }
  }, [productId, logoImageId, productImageId, savingRoles]);

  const startBannerGeneration = useCallback(async () => {
    if (!productId) throw new Error('product missing');
    try {
      const response = await signupOnboardingService.generateBanners(productId);
      setSessionId(response.session_id);
      setStep('banner-grid');
    } catch (err: any) {
      if (err?.response?.status === 409) {
        await completeAndOpenPlans();
        return;
      }
      throw err;
    }
  }, [productId, completeAndOpenPlans]);

  if (!user || !guardReady)
    return (
      <div className={PAGE}>
        <main className={`${CARD_BASE} ${CARD_NARROW}`}>
          <div className={LOGO}>
            <Image category="common" fileName="logo.png" alt="Tasky" />
          </div>
          <div className={CENTER}>読み込み中...</div>
        </main>
      </div>
    );
  const idx = visualIndex(step);
  const wide = [
    'logo-select',
    'product-image-select',
    'researching',
    'product-review',
    'banner-grid',
  ].includes(step);
  const render = () => {
    switch (step) {
      case 'url-input':
        return (
          <>
            {creating ? (
              <div className={CENTER}>サービスを登録しています...</div>
            ) : (
              <UrlInputStep onNext={handleUrl} initialUrl={serviceUrl} />
            )}
            <button
              className={SKIP}
              type="button"
              onClick={() => setSkipOpen(true)}
            >
              スキップ
            </button>
          </>
        );
      case 'image-select':
        return (
          <>
            <div className={IMAGE_INTRO}>
              <h2 className={INTRO_TITLE}>バナーに使う候補画像を保存</h2>
              <p className={INTRO_COPY}>
                URLから見つかった画像を確認し、使用したい候補を保存してください。
                <br />
                次の画面でロゴと商品画像を1枚ずつ指定します。
              </p>
              <button
                className={PRIMARY}
                type="button"
                onClick={() => setImageModalOpen(true)}
              >
                画像一覧を開く
              </button>
            </div>
            {productId && (
              <UrlImageSelectionModal
                isOpen={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                url={serviceUrl}
                productId={productId}
                onComplete={handleImagesSaved}
                onSkip={() => handleImagesSaved([])}
              />
            )}
            <button
              className={SKIP}
              type="button"
              onClick={() => setSkipOpen(true)}
            >
              スキップ
            </button>
          </>
        );
      case 'logo-select':
        return productId ? (
          <>
            <SignupImageRoleStep
              role="logo"
              images={images}
              selectedId={logoImageId}
              productId={productId}
              onSelect={setLogoImageId}
              onUploaded={(image) => {
                setImages((prev) => [...prev, image]);
                setLogoImageId(image.id);
              }}
              onBack={() => setStep('image-select')}
              onNext={() => setStep('product-image-select')}
            />
            <button
              className={SKIP}
              type="button"
              onClick={() => setSkipOpen(true)}
            >
              スキップ
            </button>
          </>
        ) : null;
      case 'product-image-select':
        return productId ? (
          <>
            <SignupImageRoleStep
              role="product"
              images={images.filter((image) => image.id !== logoImageId)}
              selectedId={
                productImageId === logoImageId ? null : productImageId
              }
              productId={productId}
              onSelect={setProductImageId}
              onUploaded={(image) => {
                setImages((prev) => [...prev, image]);
                setProductImageId(image.id);
              }}
              onBack={() => setStep('logo-select')}
              onNext={persistRolesAndResearch}
              nextLoading={savingRoles}
              nextLoadingMessage="選んだ画像を保存しています。画面が切り替わるまでそのままお待ちください。"
            />
            <button
              className={SKIP}
              type="button"
              onClick={() => setSkipOpen(true)}
            >
              スキップ
            </button>
          </>
        ) : null;
      case 'researching':
        return productId ? (
          <SignupResearchStep
            productId={productId}
            onComplete={() => setStep('product-review')}
            onSkip={() => setSkipOpen(true)}
          />
        ) : null;
      case 'product-review':
        return productId ? (
          <SignupProductReviewStep
            productId={productId}
            onGenerate={startBannerGeneration}
            onSkip={() => setSkipOpen(true)}
          />
        ) : null;
      case 'banner-grid':
        return sessionId ? (
          <SignupBannerGridStep
            sessionId={sessionId}
            onContinue={completeAndOpenPlans}
            onSkip={() => setSkipOpen(true)}
          />
        ) : (
          <div className={CENTER}>生成を準備しています...</div>
        );
    }
  };
  return (
    <div className={PAGE}>
      <main className={`${CARD_BASE} ${wide ? CARD_WIDE : CARD_NARROW}`}>
        <div className={LOGO}>
          <Image category="common" fileName="logo.png" alt="Tasky" />
        </div>
        <div className={INDICATOR} data-testid="step-indicator">
          {VISUAL_STEPS.map((item, index) => (
            <React.Fragment key={item.label}>
              {index > 0 && (
                <div
                  className={`${LINE_BASE} ${
                    idx >= index ? LINE_DONE : LINE_TODO
                  }`}
                />
              )}
              <div className={INDICATOR_ITEM}>
                <div
                  className={`${DOT_BASE} ${
                    idx === index || idx > index ? DOT_ON : DOT_OFF
                  } ${idx === index ? DOT_ACTIVE_RING : DOT_NO_RING}`}
                />
                <span
                  className={`${STEP_LABEL_BASE} ${
                    idx === index || idx > index
                      ? STEP_LABEL_ON
                      : STEP_LABEL_OFF
                  } ${
                    idx === index
                      ? STEP_LABEL_ACTIVE_WEIGHT
                      : STEP_LABEL_IDLE_WEIGHT
                  }`}
                >
                  {item.label}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>
        {error && (
          <div className={ERROR_BANNER} role="alert">
            {error}
          </div>
        )}
        <div className={CONTENT} key={step} data-testid={`step-${step}`}>
          {render()}
        </div>
      </main>
      <SignupSkipConfirmation
        open={skipOpen}
        busy={skipping}
        onCancel={() => setSkipOpen(false)}
        onConfirm={confirmSkip}
      />
    </div>
  );
};
export default SignupOnboardingPage;
